import { useToggle } from 'ahooks';
import { Button, Empty, message, Modal, Spin } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { queries } from 'services/queries';
import { useQuery } from '@tanstack/react-query';
import { CouponItem } from './CouponItem';
import { useCouponPickerContext } from './CouponPickerContext';

interface ExpireCouponModalProps {
  conversation: Conversation.Conversation;
}

export const ExpireCouponModal = (props: ExpireCouponModalProps) => {
  const { conversation } = props;
  const { storeId } = conversation;
  const [visible, { toggle }] = useToggle(false);
  const { t } = useTranslate();
  const { openCopy } = useCouponPickerContext()!;

  const {
    data: endedCouponsData,
    isLoading: endedLoading,
    isError: endedError,
  } = useQuery({
    ...queries.coupons.list({
      internal_store_id: storeId,
      voucher_status: 'ENDED' as Conversation.Voucher.Status,
    }),
    keepPreviousData: true,
    enabled: visible,
    onError: () => {
      message.error(t('chatWorkspace.expiredCouponList.toast.fetchFailed'));
    },
  });

  const endedCoupons = useMemo(() => endedCouponsData?.data?.data || [], [endedCouponsData]);

  const handleCopyAndCreate = useCallback(
    (coupon: Conversation.Voucher.Vo) => {
      toggle();
      openCopy(coupon);
    },
    [openCopy, toggle],
  );

  let content = null;

  if (endedLoading) {
    content = (
      <div className="flex justify-center py-6">
        <Spin />
      </div>
    );
  } else if (endedError) {
    content = (
      <div className="flex justify-center py-6">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('chatWorkspace.expiredCouponList.toast.fetchFailed')}
        />
      </div>
    );
  } else if (endedCoupons?.length === 0) {
    content = (
      <div className="flex justify-center py-6">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('chatWorkspace.expiredCouponList.empty')}
        />
      </div>
    );
  } else {
    content = endedCoupons?.map((coupon) => (
      <CouponItem
        key={coupon.internalVoucherId}
        coupon={coupon}
        conversation={conversation}
        onCopyAndCreate={handleCopyAndCreate}
      />
    ));
  }

  return (
    <>
      <Button type="text" onClick={toggle}>
        {t('chatWorkspace.couponPicker.button.viewExpired')}
      </Button>
      <Modal
        title={t('chatWorkspace.expiredCouponList.title')}
        visible={visible}
        centered
        onCancel={toggle}
        zIndex={1050}
        footer={null}
        width={560}
        bodyStyle={{
          maxHeight: '80vh',
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {content}
      </Modal>
    </>
  );
};
